import {
  collectionScope,
  Currency,
  getMerchantStructureByMerchantId,
  LoanStatusExternal,
} from '@nemuru-eng/js-api-client';
import { useMemo, useState } from 'react';

import { ChannelAndAgentMetrics } from '@/components/modules/Metrics/ChannelAgentMetricViews/components/ChannelAndAgentMetrics';
import { MetricsControllers } from '@/components/modules/Metrics/ChannelAgentMetricViews/components/MetricsControllers';
import { loanStatusExternalValues } from '@/components/modules/Metrics/ChannelAgentMetricViews/constants/LoanStatusExternalValues';
import { MerchantChannelAgentMetricViews } from '@/components/modules/Metrics/shared/components/MerchantChannelAgentMetricViews';
import { useLiveMetrics } from '@/components/modules/Metrics/shared/hooks/useLiveMetrics';
import { useMetricMerchantChannelAgentViews } from '@/components/modules/Metrics/shared/hooks/useMetricMerchantChannelAgentViews';
import { useMetricsScope } from '@/components/modules/Metrics/shared/hooks/useMetricsScope';
import { useTimeInterval } from '@/components/modules/Metrics/shared/hooks/useTimeInterval';
import { ScoreCardMetricKey } from '@/components/modules/Metrics/shared/types/ScoreCardMetricKey';
import { PendingDataWarning } from '@/components/shared/components/PendingDataWarning';
import { PendingDataType } from '@/config/types';
import { useCache } from '@/hooks/requests/useCache';
import { useUser } from '@/hooks/useUser';

const currency = Currency.EUR;

interface MetricMerchantViewsWrapperProps {
  merchantId: string;
}

export const MetricMerchantViewsWrapper = ({ merchantId }: MetricMerchantViewsWrapperProps) => {
  const { isUserAgent } = useUser();

  const { data: merchantStructure } = useCache(getMerchantStructureByMerchantId, { merchantId });

  const { timeInterval, setTimeInterval } = useTimeInterval();
  const [selectedLoanStatusExternal, setSelectedLoanStatusExternal] = useState<LoanStatusExternal>(
    LoanStatusExternal.CONFIRMED,
  );
  const [selectedMetricsType, setSelectedMetricsType] = useState<ScoreCardMetricKey>(
    ScoreCardMetricKey.totalPrincipalAmount,
  );

  const merchantIds = useMemo(() => [merchantId], [merchantId]);

  const { filteredMetrics, hasRequested } = useMetricMerchantChannelAgentViews({
    currency,
    timeInterval,
    loanStatusExternal: loanStatusExternalValues,
    merchantIds,
  });

  const merchantScope = useMemo(() => {
    try {
      return collectionScope.merchant(merchantId);
    } catch {
      return undefined;
    }
  }, [merchantId]);
  useLiveMetrics(useMetricsScope(merchantScope));

  if (!timeInterval || !merchantStructure) {
    return <PendingDataWarning type={PendingDataType.NO_METRICS_INFORMATION} />;
  }

  return (
    <>
      <MetricsControllers
        selectedLoanStatusExternal={selectedLoanStatusExternal}
        setSelectedLoanStatusExternal={!isUserAgent ? setSelectedLoanStatusExternal : undefined}
        selectedMetricsType={selectedMetricsType}
        setSelectedMetricsType={setSelectedMetricsType}
        timeInterval={timeInterval}
        setTimeInterval={setTimeInterval}
      />
      <MerchantChannelAgentMetricViews
        currency={currency}
        selectedMetricsType={selectedMetricsType}
        timeInterval={timeInterval}
        loanStatusExternal={loanStatusExternalValues}
        filteredMetrics={filteredMetrics}
        hasRequested={hasRequested}
        setSelectedLoanStatusExternal={!isUserAgent ? setSelectedLoanStatusExternal : undefined}
      />
      {!isUserAgent && (
        <ChannelAndAgentMetrics
          merchantStructure={merchantStructure}
          timeInterval={timeInterval}
          setTimeInterval={setTimeInterval}
          filteredMetrics={filteredMetrics}
          hasRequested={hasRequested}
          currency={currency}
          selectedLoanStatusExternal={selectedLoanStatusExternal}
          selectedMetricsType={selectedMetricsType}
        />
      )}
    </>
  );
};
